import { ReactElement, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, FormControl, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { IAcquisitionVariableFullData } from "../../../../../types";
import { IRadarLimit, IRadarVariable } from "../../../types";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { cloneObject } from "../../../../../system/utils";
import StyledTableRow from "../../../../../components/styledTableRow";


interface IProps {
  variable: IRadarVariable;
  metaData: IAcquisitionVariableFullData;
  onAccept: (variable: IRadarVariable) => void;
  onCancel: () => void;
  machineVariables: IAcquisitionVariableFullData[];
  tag: IAcquisitionVariableFullData | null;
}

interface ILimitProps {
  title: string;
  limit: IRadarLimit;
  machineVariables: IAcquisitionVariableFullData[];
  updateLimit: (limit: IRadarLimit) => void;
  level: -1 | 0 | 1;
  tag: IAcquisitionVariableFullData | null;
}

const LimitComponent = ({ title, limit, machineVariables, updateLimit, level, tag }: ILimitProps): ReactElement => {
  const limitTag = limit && limit.type === "TAG" ? machineVariables.find(x => x.code === limit.value) ?? null : null;

  const onChange = (field: keyof IRadarLimit, value: any) => {
    if (field === "severity") {
      limit[field] = value !== "none" ? value : null;
    } else if (field === "type") {
      limit.type = value;
      if (tag && limit.type === "VALUE") {
        if(level === 1 && tag.axisMaximum) {
          limit.value = tag.axisMaximum;
        } else if(level === -1 && tag.axisMinimum) {
          limit.value = tag.axisMinimum;
        }
      }
    } else if (field === "value") {
      limit.value = value;
    }

    updateLimit(limit);
  };

  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} >
      <TableCell>
        {title}
      </TableCell>

      <TableCell>
        <Select
          sx={{ width: 100 }}
          variant="standard"
          value={limit?.type ?? "VALUE"}
          displayEmpty={true}
          onChange={(event: SelectChangeEvent) => onChange("type", event.target.value as string)}>
          <MenuItem value={"TAG"}>TAG</MenuItem>
          <MenuItem value={"VALUE"}>VALUE</MenuItem>
          <MenuItem value={"NONE"}>NONE</MenuItem>
        </Select>
      </TableCell>

      <TableCell>
        {limit.type === "TAG" &&
          <Autocomplete
            sx={{ width: 300 }}
            options={machineVariables}
            getOptionLabel={(option: IAcquisitionVariableFullData) => option.name}
            value={limitTag}
            openOnFocus={true}
            onChange={(_event: any, newValue: IAcquisitionVariableFullData | null) => {
              onChange("value", newValue?.code);
            }}
            renderInput={(params) =>
              <TextField {...params} variant="standard" color={"primary"}/>
            }
          />
        }

        {limit.type === "VALUE" &&
          <TextField
            sx={{ width: 300 }}
            variant="standard"
            size="small"
            value={(limit.value ?? 0) as number}
            type="number"
            onChange={(event) => onChange("value", parseFloat(event.target.value as string))}
          />
        }
      </TableCell>

      <TableCell>
        {limit.comparison && limit?.type !== "NONE" &&
          <FormControl className="w-100">
            <Select
              variant="standard"
              value={limit.severity ?? "none"}
              displayEmpty={true}
              onChange={(event) => onChange("severity", event.target.value as string)}>
              <MenuItem value={"none"}>NONE</MenuItem>
              <MenuItem value={"error"}>ERROR</MenuItem>
              <MenuItem value={"warning"}>WARNING</MenuItem>
              <MenuItem value={"info"}>INFO</MenuItem>
            </Select>
          </FormControl>
        }
      </TableCell>
    </StyledTableRow>
  );
};

const VariableConfigDialog = ({ variable, onAccept, machineVariables, onCancel, tag }: IProps): ReactElement => {
  const [ data, setData ] = useState(cloneObject(variable));


  const onInternalAccept = () => {
    onAccept(data);
  };

  const onUpdateLimit = (field: string, value: IRadarLimit) => {
    setData({ ...data, limits: { ...data.limits, [field]: value } });
  };

  return (
    <Dialog onClose={onCancel} aria-labelledby="customized-dialog-title" open={true} maxWidth="md" keepMounted={true}>
      <DialogTitle id="customized-dialog-title">
        Variable Config
      </DialogTitle>
      <DialogContent dividers={true} >
        <Table stickyHeader={true} size={"small"} >
          <TableHead>
            <TableRow>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Limit</TableCell>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Type</TableCell>
              <TableCell className="table-header" style={{ minWidth: 300 }}>Value</TableCell>
              <TableCell className="table-header" style={{ minWidth: 300 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <LimitComponent title="Maximum" machineVariables={machineVariables} limit={data.limits.maximum} updateLimit={value => onUpdateLimit("maximum", value)} level={1} tag={tag}/>
            <LimitComponent title="Aim" machineVariables={machineVariables} limit={data.limits.aim}  updateLimit={value => onUpdateLimit("aim", value)} level={0}  tag={null}/>
            <LimitComponent title="Minimum" machineVariables={machineVariables} limit={data.limits.minimum}  updateLimit={value => onUpdateLimit("minimum", value)} level={-1} tag={tag}/>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onInternalAccept} variant="contained" color="primary" startIcon={<CheckIcon />}>
          Accept
        </Button>
        <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariableConfigDialog;
